/**
 * GELİR MODELİ — sertifika, aktivasyon kodu, oturum jetonu, kabiliyetler
 * (kullanıcı kararı 20.10.2026; `claude/notlar/2026-10-26-gelir-modeli-kararlar.md`).
 *
 * Bu modül kapının (sertifika basar, jeton verir), fiyat sunucusunun (jetonu
 * doğrular, sınırı uygular) ve cihazın (sertifikayı kasada tutar, ekrana
 * "bu açık mı?" diye sorar) ORTAK dilidir. Kural tek kopya: bir kasada ne
 * açık olduğunu yalnızca `etkinKabiliyetler` + `sinirlar` söyler.
 *
 * Gizlilik sınırı:
 *   - Sertifika kasaya (kasa kimliği) bağlıdır, kişiye değil. İçinde ad,
 *     e-posta, ödeme, cihaz YOK.
 *   - Jeton yalnızca kabiliyetleri ve bitişi taşır; sertifika numarası ve
 *     kasa kimliği YOK. Fiyat sunucusu jetondan kasayı çıkaramaz.
 *   - Kasa kimliği kasa kodundan (Parafon sunucusu yedeği) AYRI bir değerdir.
 *
 * Kriptografi: mühür Ed25519 (WebCrypto). Sertifikanın mühürü ile jetonun
 * mühürü AYRI anahtarlardır; gerçek ve test mühürleri de ayrı anahtarlardır
 * ve gövdede yazılıdır — gerçek kapı test mühürlü sertifikayı tanımaz.
 */

import { KASA_KODU_ALFABESI } from "@parafon/contracts/central-api";

// ---------------------------------------------------------------------------
// Kabiliyetler — kapalı liste
// ---------------------------------------------------------------------------

/**
 * `acik`: var ya da yok. `sayi`: kaç tane alındığı (sınır = adet).
 * Ekrandaki ad kısa; açıklaması satış ekranının işi.
 *
 * Sıra satış ekranındaki sıradır: önce geçiş paketi, sonra ekler.
 */
export const KABILIYETLER = [
  { ad: "gecis", tur: "acik", etiket: "Ücretliye geçiş paketi" },
  { ad: "ek-hesap", tur: "sayi", etiket: "Ek hesap" },
  { ad: "ek-oturum", tur: "sayi", etiket: "Ek oturum" },
  { ad: "ek-kasa", tur: "sayi", etiket: "Ek kasa" },
  { ad: "fiyat-gecmisi", tur: "sayi", etiket: "Fiyat geçmişi (5 yıllık dilim)" },
  { ad: "gecmis-gun", tur: "acik", etiket: "Varlık Dökümü: geçmiş bir gün" },
  { ad: "donemsel-getiri", tur: "acik", etiket: "Dönemsel Getiri" },
  { ad: "sanal-hesap", tur: "sayi", etiket: "Sanal hesap (üçlü paket)" },
  { ad: "sanal-b", tur: "acik", etiket: "Sanal hesap: B tipi" },
  { ad: "sanal-dengeleme", tur: "acik", etiket: "Sanal hesap: dengeleme" },
  { ad: "sanal-paylasim", tur: "acik", etiket: "Sanal hesap: paylaşım" },
  { ad: "ice-aktarma", tur: "acik", etiket: "İçe aktarma (CSV)" },
  { ad: "kurum-ekstresi", tur: "acik", etiket: "Kurum ekstresinden alma" },
  { ad: "depolama-bulut", tur: "acik", etiket: "Kendi buluta otomatik yedek" },
  { ad: "depolama-sunucu", tur: "acik", etiket: "Parafon sunucusuna yedek" },
  { ad: "depolama-kota", tur: "sayi", etiket: "Yedek kotası" },
  { ad: "surum-gecmisi", tur: "acik", etiket: "Yedek sürüm geçmişi" },
] as const;

export type KabiliyetAdi = (typeof KABILIYETLER)[number]["ad"];

export const KABILIYET_ADLARI: ReadonlySet<string> = new Set(KABILIYETLER.map((k) => k.ad));

const KABILIYET_TURU: ReadonlyMap<string, "acik" | "sayi"> = new Map(KABILIYETLER.map((k) => [k.ad, k.tur]));

// ---------------------------------------------------------------------------
// Sabitler
// ---------------------------------------------------------------------------

/** Ücretsiz kasa (sertifikasız ya da geçiş paketi süresi dolmuş). */
export const UCRETSIZ = {
  hesap: 2,
  hesapBasinaVarlik: 5,
  kasa: 1,
  fiyatGecmisiYil: 1,
  donemselVarlik: 3,
  /** Jetonsuz istek son TAM SAATLİK yuvanın fiyatını alır (kullanıcı, 26.10). */
  fiyatYuvaDakika: 60,
} as const;

/** Geçiş paketinin içeriği (kararın "Ücretliye geçiş paketi" maddesi). */
export const GECIS_PAKETI = {
  fiyatGecmisiYil: 5,
  fiyatYuvaDakika: 30,
  oturum: 2,
  sanalHesap: 1,
} as const;

/** Bir `fiyat-gecmisi` dilimi kaç yıl ekler; bir `sanal-hesap` paketi kaç hesap. */
export const DILIM = { fiyatGecmisiYil: 5, sanalHesapPaketi: 3 } as const;

/** Kurucu sertifikasının oturum sınırı ("yüksek oturum sınırı"). */
export const KURUCU_OTURUM = 10;

export const OTURUM_SURELERI = {
  /** Jeton bu kadar saniye geçerli. */
  jetonSaniye: 60 * 60,
  /** Uygulama açıkken jeton bu aralıkla yenilenir. */
  yenilemeSaniye: 15 * 60,
  /** Yenilenmeyen oturum bu kadar saniye sonra sayımdan düşer. */
  dusmeSaniye: 60 * 60,
  /** Ücretli özellikler son sunucu teyidinden bu kadar saat sonra kapanır. */
  cevrimdisiSaat: 48,
} as const;

/** Ücretli isteklerde jetonun taşındığı başlık. Gövdede taşınmaz (beyaz liste). */
export const JETON_BASLIGI = "X-Parafon-Jeton";

// ---------------------------------------------------------------------------
// Kimlikler ve kodlar
// ---------------------------------------------------------------------------

/**
 * Kasa kimliği: kasa kurulunca (ya da ilk sertifikada) cihazda üretilen 26
 * karakter (~130 bit), `KASA_KODU_ALFABESI`nden. Kasanın şifreli kabında
 * durur; kasa kodundan AYRIDIR (kasa kodu yedeği açar, kasa kimliği yalnızca
 * sertifikayı bağlar).
 */
export const KASA_KIMLIGI_UZUNLUGU = 26;

/** Aktivasyon kodu: 16 karakter, "XXXX-XXXX-XXXX-XXXX" gösterilir; tek kullanımlık. */
export const AKTIVASYON_KODU_UZUNLUGU = 16;

/** Sertifika numarası: 12 karakter; yalnızca kapıda ve yönetim sayfasında görünür. */
export const SERTIFIKA_NO_UZUNLUGU = 12;

const ALFABE_KUMESI = new Set(KASA_KODU_ALFABESI);

function alfabedenMi(s: string, uzunluk: number): boolean {
  return s.length === uzunluk && [...s].every((c) => ALFABE_KUMESI.has(c));
}

export function kasaKimligiGecerli(s: unknown): s is string {
  return typeof s === "string" && alfabedenMi(s, KASA_KIMLIGI_UZUNLUGU);
}

/**
 * Kullanıcının yazdığı aktivasyon kodunu düzeltir: tire/boşluk atılır, büyük
 * harfe çevrilir, karışan harfler (O→0, I/L→1, U→V) alfabeye çekilir.
 * Geçersizse `null`.
 */
export function aktivasyonKoduDuzelt(girdi: string): string | null {
  const ham = girdi
    .toUpperCase()
    .replace(/[\s-]/g, "")
    .replace(/O/g, "0")
    .replace(/[IL]/g, "1")
    .replace(/U/g, "V");
  return alfabedenMi(ham, AKTIVASYON_KODU_UZUNLUGU) ? ham : null;
}

export function aktivasyonKoduGoster(kod: string): string {
  return kod.replace(/(.{4})(?=.)/g, "$1-");
}

/** Rastgele kimlik/kod (kasa kimliği, aktivasyon kodu, sertifika no). */
export function rastgeleKod(uzunluk: number): string {
  // 256, 32'nin katı: modülo yanlılığı yok.
  const baytlar = new Uint8Array(uzunluk);
  globalThis.crypto.getRandomValues(baytlar);
  let s = "";
  for (const b of baytlar) s += KASA_KODU_ALFABESI[b % KASA_KODU_ALFABESI.length];
  return s;
}

// ---------------------------------------------------------------------------
// Sertifika
// ---------------------------------------------------------------------------

export const SERTIFIKA_TURLERI = ["satis", "kurucu", "hediye"] as const;
export type SertifikaTuru = (typeof SERTIFIKA_TURLERI)[number];

/** `gercek` mühürlü sertifikayı yalnızca gerçek kapı basar; `test` gerçek ortamda geçmez. */
export const MUHURLER = ["gercek", "test"] as const;
export type Muhur = (typeof MUHURLER)[number];

export interface SertifikaKabiliyeti {
  readonly ad: KabiliyetAdi;
  /** `sayi` türünde adet (≥ 1); `acik` türünde yazılmaz. */
  readonly adet?: number;
}

/**
 * Mühürlenen gövde. Günler "YYYY-MM-DD" (UTC). `bitis` o gün DAHİL geçerli;
 * kurucu sertifikasında `null` (süresiz). Kurucu sertifikası bütün
 * kabiliyetleri (sonradan eklenenler dahil) açar; `kabiliyetler` boş kalır.
 */
export interface SertifikaGovdesi {
  readonly surum: 1;
  readonly no: string;
  readonly tur: SertifikaTuru;
  readonly muhur: Muhur;
  readonly kasaKimligi: string;
  readonly kabiliyetler: readonly SertifikaKabiliyeti[];
  readonly basim: string;
  readonly bitis: string | null;
}

const GUN = /^\d{4}-\d{2}-\d{2}$/;

function gunGecerli(s: unknown): s is string {
  if (typeof s !== "string" || !GUN.test(s)) return false;
  const t = new Date(`${s}T00:00:00Z`);
  return !Number.isNaN(t.getTime()) && t.toISOString().slice(0, 10) === s;
}

/** Bilinmeyen alan da reddedilir: gövde mühürlüdür, fazlası bir şey saklar. */
function alanlarTam(o: object, izinli: readonly string[]): boolean {
  return Object.keys(o).every((k) => izinli.includes(k));
}

/** Kapı basmadan, cihaz kaydetmeden önce AYNI denetim. `null` = uygun. */
export function sertifikaGovdesiDenetle(g: unknown): string | null {
  if (g === null || typeof g !== "object" || Array.isArray(g)) return "gövde nesne değil";
  const s = g as Record<string, unknown>;
  if (!alanlarTam(s, ["surum", "no", "tur", "muhur", "kasaKimligi", "kabiliyetler", "basim", "bitis"])) {
    return "tanınmayan alan";
  }
  if (s["surum"] !== 1) return "tanınmayan sürüm";
  if (typeof s["no"] !== "string" || !alfabedenMi(s["no"], SERTIFIKA_NO_UZUNLUGU)) return "sertifika numarası geçersiz";
  if (!(SERTIFIKA_TURLERI as readonly unknown[]).includes(s["tur"])) return "sertifika türü geçersiz";
  if (!(MUHURLER as readonly unknown[]).includes(s["muhur"])) return "mühür geçersiz";
  if (!kasaKimligiGecerli(s["kasaKimligi"])) return "kasa kimliği geçersiz";
  if (!gunGecerli(s["basim"])) return "basım günü geçersiz";
  if (s["tur"] === "kurucu") {
    if (s["bitis"] !== null) return "kurucu sertifikası süresizdir";
  } else {
    if (!gunGecerli(s["bitis"])) return "bitiş günü geçersiz";
    if ((s["bitis"] as string) < (s["basim"] as string)) return "bitiş basımdan önce";
  }
  const k = s["kabiliyetler"];
  if (!Array.isArray(k)) return "kabiliyetler liste değil";
  if (s["tur"] === "kurucu" && k.length > 0) return "kurucu sertifikası kabiliyet saymaz";
  if (s["tur"] !== "kurucu" && k.length === 0) return "kabiliyet yok";
  const gorulen = new Set<string>();
  for (const x of k as unknown[]) {
    if (x === null || typeof x !== "object" || !alanlarTam(x, ["ad", "adet"])) return "kabiliyet biçimi geçersiz";
    const { ad, adet } = x as { ad?: unknown; adet?: unknown };
    if (typeof ad !== "string" || !KABILIYET_ADLARI.has(ad)) return `tanınmayan kabiliyet: ${String(ad)}`;
    if (gorulen.has(ad)) return `kabiliyet iki kez: ${ad}`;
    gorulen.add(ad);
    if (KABILIYET_TURU.get(ad) === "sayi") {
      if (typeof adet !== "number" || !Number.isInteger(adet) || adet < 1 || adet > 1000) return `adet geçersiz: ${ad}`;
    } else if (adet !== undefined) {
      return `adet alınmaz: ${ad}`;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Mühürlü metin: "<önek>.<gövde base64url>.<imza base64url>"
// ---------------------------------------------------------------------------

export const SERTIFIKA_ONEKI = "PFS1";
export const JETON_ONEKI = "PFJ1";

/**
 * Kanonik JSON: anahtarlar sıralı, boşluksuz. Mühür bu metnin baytlarının
 * üstüne vurulur; kapı ve doğrulayan aynı metni üretmek zorunda.
 */
export function kanonikJson(v: unknown): string {
  if (v === null || typeof v !== "object") return JSON.stringify(v);
  if (Array.isArray(v)) return `[${v.map(kanonikJson).join(",")}]`;
  const o = v as Record<string, unknown>;
  return `{${Object.keys(o)
    .filter((k) => o[k] !== undefined)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${kanonikJson(o[k])}`)
    .join(",")}}`;
}

export function base64url(baytlar: Uint8Array): string {
  let s = "";
  for (const b of baytlar) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function base64urlCoz(s: string): Uint8Array | null {
  if (!/^[A-Za-z0-9_-]*$/.test(s)) return null;
  try {
    const ikili = atob(s.replace(/-/g, "+").replace(/_/g, "/") + "===".slice((s.length + 3) % 4));
    return Uint8Array.from(ikili, (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
}

/**
 * WebCrypto anahtarı. Tarayıcıda `CryptoKey`, Node'da `webcrypto.CryptoKey`;
 * adı bu yüzden çalışma ortamının kendi `subtle.sign` imzasından alınır
 * (merkez paketinin tip ayarında DOM yok).
 */
export type MuhurAnahtari = Parameters<typeof globalThis.crypto.subtle.sign>[1];

export interface MuhurluParcalar {
  /** Mühürlenen bayt dizisi: `<önek>.<gövde base64url>` (önek de mühürün içinde). */
  readonly imzalanan: Uint8Array;
  readonly govde: unknown;
  readonly imza: Uint8Array;
}

/** Biçimi ayırır; mühürü DOĞRULAMAZ (`muhurDogrula`). */
export function muhurluAyristir(metin: string, onek: string): MuhurluParcalar | null {
  const p = metin.split(".");
  if (p.length !== 3 || p[0] !== onek) return null;
  const govdeBayt = base64urlCoz(p[1]!);
  const imza = base64urlCoz(p[2]!);
  if (govdeBayt === null || imza === null || imza.length !== 64) return null;
  let govde: unknown;
  try {
    govde = JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(govdeBayt));
  } catch {
    return null;
  }
  // Mühürlü gövde kanonik olmak zorunda: aynı içeriğin iki farklı yazımı olmasın.
  if (kanonikJson(govde) !== new TextDecoder().decode(govdeBayt)) return null;
  return { imzalanan: new TextEncoder().encode(`${p[0]}.${p[1]}`), govde, imza };
}

/** Kapı (ve testler) için: gövdeyi mühürler. Anahtar Ed25519 özel anahtar. */
export async function muhurle(govde: unknown, onek: string, ozelAnahtar: MuhurAnahtari): Promise<string> {
  const g = base64url(new TextEncoder().encode(kanonikJson(govde)));
  const imzalanan = new TextEncoder().encode(`${onek}.${g}`);
  const imza = new Uint8Array(await globalThis.crypto.subtle.sign("Ed25519", ozelAnahtar, imzalanan));
  return `${onek}.${g}.${base64url(imza)}`;
}

export async function muhurDogrula(p: MuhurluParcalar, acikAnahtar: MuhurAnahtari): Promise<boolean> {
  return globalThis.crypto.subtle.verify(
    "Ed25519",
    acikAnahtar,
    p.imza as Uint8Array<ArrayBuffer>,
    p.imzalanan as Uint8Array<ArrayBuffer>,
  );
}

// ---------------------------------------------------------------------------
// Etkin kabiliyetler ve sınırlar — TEK KURAL
// ---------------------------------------------------------------------------

/** `acik` için 1, `sayi` için toplam adet. Kurucu sertifikasında hepsi `null` = sınırsız. */
export type EtkinKabiliyetler = Readonly<Partial<Record<KabiliyetAdi, number | null>>>;

/**
 * Bir kasanın elindeki sertifika gövdelerinden bugün açık olanlar.
 *   - Başka kasaya, başka mühüre ait ya da süresi geçmiş gövde sayılmaz.
 *   - Kurucu sertifikası her şeyi sınırsız açar.
 *   - Geçiş paketi yoksa (ya da süresi dolduysa) hiçbir ek açık değildir —
 *     ekler geçiş paketinin üstüne alınır.
 *   - Aynı kabiliyet birden çok sertifikadaysa `sayi` toplanır.
 *
 * Gövdelerin mühürü bu fonksiyondan ÖNCE doğrulanmış olmalı.
 */
export function etkinKabiliyetler(
  govdeler: readonly SertifikaGovdesi[],
  kasaKimligi: string,
  muhur: Muhur,
  bugun: string,
): EtkinKabiliyetler {
  const gecerli = govdeler.filter(
    (g) => g.kasaKimligi === kasaKimligi && g.muhur === muhur && g.basim <= bugun && (g.bitis === null || bugun <= g.bitis),
  );
  if (gecerli.some((g) => g.tur === "kurucu")) {
    return Object.fromEntries(KABILIYETLER.map((k) => [k.ad, null])) as EtkinKabiliyetler;
  }
  const sonuc: Partial<Record<KabiliyetAdi, number>> = {};
  for (const g of gecerli) {
    for (const k of g.kabiliyetler) {
      sonuc[k.ad] = (sonuc[k.ad] ?? 0) + (k.adet ?? 1);
    }
  }
  if (sonuc.gecis === undefined) return {};
  if (sonuc.gecis > 1) sonuc.gecis = 1;
  for (const k of KABILIYETLER) {
    if (k.tur === "acik" && (sonuc[k.ad] ?? 0) > 1) sonuc[k.ad] = 1;
  }
  return sonuc;
}

/** `null` = sınırsız. Ekranlar ve sunucu YALNIZCA buna bakar. */
export interface Sinirlar {
  readonly ucretli: boolean;
  readonly hesap: number | null;
  /** Hesap başına farklı varlık; ilk ek hesapla kalkar. */
  readonly hesapBasinaVarlik: number | null;
  readonly kasa: number | null;
  readonly oturum: number;
  /** Fiyat geçmişi derinliği (yıl). */
  readonly fiyatGecmisiYil: number | null;
  readonly fiyatYuvaDakika: 30 | 60;
  readonly asilGetiri: boolean;
  readonly gecmisGun: boolean;
  /** Dönemsel Getiri: `ucretsiz` en çok 3, `kendi` kendi varlıklarının hepsi, `sinirsiz` + kıyas ve sepet. */
  readonly donemsel: "ucretsiz" | "kendi" | "sinirsiz";
  readonly sanalHesap: number | null;
  readonly sanalB: boolean;
  readonly sanalDengeleme: boolean;
  readonly sanalPaylasim: boolean;
  readonly iceAktarma: boolean;
  readonly kurumEkstresi: boolean;
  readonly depolama: "klasor" | "bulut" | "sunucu";
  readonly depolamaKota: number | null;
  readonly surumGecmisi: boolean;
}

export function sinirlar(e: EtkinKabiliyetler): Sinirlar {
  const kurucu = e.gecis === null;
  const var_ = (ad: KabiliyetAdi): boolean => ad in e;
  const adet = (ad: KabiliyetAdi): number => e[ad] ?? 0;
  if (!var_("gecis")) {
    return {
      ucretli: false,
      hesap: UCRETSIZ.hesap,
      hesapBasinaVarlik: UCRETSIZ.hesapBasinaVarlik,
      kasa: UCRETSIZ.kasa,
      oturum: 0,
      fiyatGecmisiYil: UCRETSIZ.fiyatGecmisiYil,
      fiyatYuvaDakika: UCRETSIZ.fiyatYuvaDakika,
      asilGetiri: false,
      gecmisGun: false,
      donemsel: "ucretsiz",
      sanalHesap: 0,
      sanalB: false,
      sanalDengeleme: false,
      sanalPaylasim: false,
      iceAktarma: false,
      kurumEkstresi: false,
      depolama: "klasor",
      depolamaKota: 0,
      surumGecmisi: false,
    };
  }
  const say = (ad: KabiliyetAdi, taban: number, carpan = 1): number | null =>
    kurucu ? null : taban + adet(ad) * carpan;
  return {
    ucretli: true,
    hesap: say("ek-hesap", UCRETSIZ.hesap),
    hesapBasinaVarlik: kurucu || adet("ek-hesap") > 0 ? null : UCRETSIZ.hesapBasinaVarlik,
    kasa: say("ek-kasa", UCRETSIZ.kasa),
    oturum: kurucu ? KURUCU_OTURUM : GECIS_PAKETI.oturum + adet("ek-oturum"),
    fiyatGecmisiYil: say("fiyat-gecmisi", GECIS_PAKETI.fiyatGecmisiYil, DILIM.fiyatGecmisiYil),
    fiyatYuvaDakika: GECIS_PAKETI.fiyatYuvaDakika,
    asilGetiri: true,
    gecmisGun: var_("gecmis-gun"),
    donemsel: var_("donemsel-getiri") ? "sinirsiz" : "kendi",
    sanalHesap: say("sanal-hesap", GECIS_PAKETI.sanalHesap, DILIM.sanalHesapPaketi),
    sanalB: var_("sanal-b"),
    sanalDengeleme: var_("sanal-dengeleme"),
    sanalPaylasim: var_("sanal-paylasim"),
    iceAktarma: var_("ice-aktarma"),
    kurumEkstresi: var_("kurum-ekstresi"),
    depolama: var_("depolama-sunucu") ? "sunucu" : var_("depolama-bulut") ? "bulut" : "klasor",
    depolamaKota: kurucu ? null : adet("depolama-kota"),
    surumGecmisi: var_("surum-gecmisi"),
  };
}

// ---------------------------------------------------------------------------
// Oturum jetonu
// ---------------------------------------------------------------------------

/**
 * Jeton gövdesi. Sertifika numarası ve kasa kimliği YOK. `son` = jetonun
 * geçerliliğinin bittiği an (ISO, UTC); `bitis` = sertifikaların en erken
 * biten gününden (ekranda "… tarihine kadar").
 */
export interface JetonGovdesi {
  readonly surum: 1;
  readonly muhur: Muhur;
  readonly kabiliyetler: EtkinKabiliyetler;
  readonly bitis: string | null;
  readonly son: string;
}

export function jetonGovdesiDenetle(g: unknown): string | null {
  if (g === null || typeof g !== "object" || Array.isArray(g)) return "gövde nesne değil";
  const s = g as Record<string, unknown>;
  if (!alanlarTam(s, ["surum", "muhur", "kabiliyetler", "bitis", "son"])) return "tanınmayan alan";
  if (s["surum"] !== 1) return "tanınmayan sürüm";
  if (!(MUHURLER as readonly unknown[]).includes(s["muhur"])) return "mühür geçersiz";
  if (s["bitis"] !== null && !gunGecerli(s["bitis"])) return "bitiş günü geçersiz";
  if (typeof s["son"] !== "string" || Number.isNaN(Date.parse(s["son"]))) return "son geçersiz";
  const k = s["kabiliyetler"];
  if (k === null || typeof k !== "object" || Array.isArray(k)) return "kabiliyetler nesne değil";
  for (const [ad, v] of Object.entries(k)) {
    if (!KABILIYET_ADLARI.has(ad)) return `tanınmayan kabiliyet: ${ad}`;
    if (v !== null && (typeof v !== "number" || !Number.isInteger(v) || v < 1)) return `adet geçersiz: ${ad}`;
  }
  return null;
}

/**
 * Fiyat sunucusunun jeton kararı: mühür doğrulandıktan SONRA çağrılır.
 * Geçersiz, süresi geçmiş ya da başka mühürlü jeton = jetonsuz istek
 * (ücretsiz sınırlar); istek reddedilmez.
 */
export function jetonSinirlari(govde: unknown, muhur: Muhur, simdi: Date): Sinirlar {
  if (jetonGovdesiDenetle(govde) !== null) return sinirlar({});
  const j = govde as JetonGovdesi;
  if (j.muhur !== muhur || Date.parse(j.son) <= simdi.getTime()) return sinirlar({});
  return sinirlar(j.kabiliyetler);
}

// ---------------------------------------------------------------------------
// Kapı uç noktaları (fiyat merkezinden AYRI hizmet; yol öneki `/kapi`)
// ---------------------------------------------------------------------------

/** `POST /kapi/etkinlestir` — kodu kullanır, sertifikayı bu kasa kimliğine basar. */
export interface EtkinlestirRequest {
  readonly kod: string;
  readonly kasaKimligi: string;
}
export interface EtkinlestirResponse {
  /** `PFS1.…` mühürlü metin; cihaz kasaya olduğu gibi yazar. */
  readonly sertifika: string;
}

/**
 * `POST /kapi/oturum` — kasa açılınca ve her `yenilemeSaniye`de. `oturum`
 * ilk istekte yok; kapı verir, cihaz yenilemede geri gönderir. Sertifika
 * olmayan kasa bu isteği HİÇ yapmaz.
 */
export interface OturumRequest {
  readonly sertifikalar: readonly string[];
  readonly oturum?: string;
}
export interface OturumResponse {
  /** `PFJ1.…` mühürlü jeton. */
  readonly jeton: string;
  readonly oturum: string;
  /** Sınır doluyken açılan oturum en eskisini düşürdüyse `true` (ekranda söylenir). */
  readonly eskisiDustu: boolean;
}

/** `POST /kapi/oturum-bitir` — kilit, otomatik kilit, profil değişimi. */
export interface OturumBitirRequest {
  readonly oturum: string;
}

/**
 * `KOD_YOK` = kod tanınmadı ya da kullanılmış (kapı ikisini ayırmaz);
 * `IPTAL` = sertifika iptal listesinde; `OTURUM_YOK` = oturum düşmüş (yeni
 * oturum açılır); `MUHUR` = tanınmayan mühür (ör. gerçek kapıda test).
 */
export type KapiHataKodu = "GECERSIZ" | "KOD_YOK" | "IPTAL" | "OTURUM_YOK" | "MUHUR" | "RATE_LIMITED";
